<template>
    <div class="scroll-inner">

      <h5 class="sub-head text-capitalize mt-0 mb-5">{{ $t('TRIP') }}</h5>
      <div class="flex-container panel-head align-top mb-10">
        <div class="flex-item">
          <p>
            <span>{{ $t('ROUTE') }} :</span>
            <span v-if="getIndentRoute.tooltip">
              <Tooltip class="item" effect="dark" placement="bottom">
                  <div slot="content" class="tooltipcontent">
                    {{getIndentRoute.tooltip}}
                  </div>
                  <strong>{{getIndentRoute.route}}</strong>
              </Tooltip>
            </span>
            <strong v-else>{{getIndentRoute.route}}</strong>
          </p>
          <p>
            <span>{{ $t('VEHICLE_NO') }} :</span>
            <strong>{{indent.vehicle_number}}</strong>
          </p>
          <p v-if="(this.indent.associated_indents && this.indent.associated_indents.length) > 0">
            <span>{{ $t('ASSOCIATED') }} :</span>
            <strong>{{getAssociated}}</strong>
            </p>
          <p v-if="indent.account_assignment_number">
            <span>
              {{ $t('ACCOUNT_ASSN_NO') }} :
              <strong><ellipsis :text="indent.account_assignment_number" :length="8" /></strong>
            </span>
          </p>
          <p v-if="indent.ses_number">
            <span>
              {{ $t('SES_NUMBER') }} :
               <strong>{{indent.ses_number}}</strong>
            </span>
          </p>
           <p v-if="indent.fo_number">
            <span>
              {{ $t('FO_NUMBER') }} : <strong>{{indent.fo_number}}</strong>
            </span>
          </p>
        </div>
        <div class="flex-item text-right ml-auto">
          <p>
            <span v-if="configurations.payment_settings &&
            !configurations.payment_settings.hide_transporter_truck_type">
              {{ $t('SYSTEM_CHARGE') }} : <strong>{{indent.truck_type}}</strong></span>
            <span v-if="indent.transporter_truck_type &&
              indent.truck_type !== indent.transporter_truck_type">
              <span>{{ $t('TRANSPORTER_CHARGE') }} : <strong>{{indent.transporter_truck_type}}</strong></span>
            </span>
          </p>
          <p v-if="indent.shipment_number">
            <span>{{ $t('INVOICE_SHIPMENT_ID') }} : <strong>{{indent.shipment_number}}</strong></span>
          </p>
          <p v-if="indent.freight_invoice_ratio">
            <span>{{ $t('FREIGHT_INVOICE_RATIO') }} : <strong>{{ Number(indent.freight_invoice_ratio).toFixed(2) }}</strong></span>
          </p>
        </div>
      </div>

      <h5 class="sub-head text-capitalize mt-0 mb-5">{{ $t('CONSIGNEES') }}</h5>
      <div class="content-boxed mx-0 mb-5" v-for="(consignee,index) in indent.consignee_data"
        :key="index">
        <div class="flex-container panel-head align-top mb-0">
          <div class="flex-item">
          <p>
            <strong>{{consignee.name}}</strong>
          </p>
          <p>
            <span>{{ $t('REFERENCE_ID') }} :</span>
            <strong>{{consignee.ref_id}}</strong>
          </p>
          </div>
          <div class="flex-item text-right ml-auto">
            <p class="list-badge">
              <span class="badge">{{ $t('WEIGHT_SHORT_FORM') }}</span>
                <label>{{consignee.weight.value}} {{consignee.weight.unit}}</label>
              <span class="badge">{{ $t('VOLUME_SHORT_FORM') }}</span>
                <label>{{fixTwoDigits(getConvertedClientVolume(consignee.volume.value))}} {{preferredVolumeUnit.toLowerCase()}}</label>
            </p>
          </div>
        </div>
        <div class="flex-container panel-head align-top mb-0">
          <div class="flex-item">
            <p>
              <strong>{{ $t('INVOICE_LR_NUMBERS') }}</strong>
            </p>
            <p v-for="(lr_number,index) in consignee.lr_numbers" :key="index">
              <span>{{lr_number}}</span>
            </p>
            <p>
              <strong>{{ $t('MATERIAL_INVOICE_NUMBER') }}</strong>
            </p>
            <p v-for="(invoiceNumber,index) in consignee.material_invoice_numbers" :key="index">
              <span>{{invoiceNumber}}</span>
            </p>
          </div>
          <div class="flex-item text-right">
            <p class="list-badge">
              <strong>{{ $t('ATTACHMENTS') }}</strong>
            </p>
            <div class="attachment-tags">
              <!-- <div class="tags-item">
                <span class="tags-icon">
                  <svgicon
                    name="pdf"
                    width="14"
                    height="14"
                    :original="true"/>
                </span>
                <span> LR_Receipt Number </span>
              </div>
              <div class="tags-item">
                <span> + 2 </span>
              </div> -->
              <div>
                <AttachmentsMin
                  v-if="isConsolidatedEnabled"
                  :attachments="getAttachments[index]"
                  :disable-upload="!$hasPermission(`transporter_add_elr_attachments_${movementSuffix}`)"
                  :multiple="true"
                  :use-modal-upload="true"
                  @addAttachments="addAttachments($event, index)"
                  @openPreviewSlider="previewConsigneeAttachments(index)"
                />
                <Attachments
                  v-else
                  :attachments="getAttachments[index]"
                  :showPreview="true"
                  :index="index"
                  :maxAllowed="lrAttachmentsLimit"
                  :disableDelete="true"
                  :disableUpload="!$hasPermission('transporter_add_elr_attachments_primary') && !$hasPermission('transporter_add_elr_attachments_secondary') && !$hasPermission('transporter_add_elr_attachments_vendor') && !$hasPermission('transporter_add_elr_attachments_return')"
                  @preview="preview"
                  @addAttachments="addAttachments($event, index)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <h5 class="sub-head text-capitalize mt-0 mb-5">{{ $t('CALCULATIONS') }}</h5>

      <div v-for="(consignee,index) in indent.consignee_data" :key="index">
          <div class="flex-container panel-head align-top mb-10" v-if="getUnitGroup(consignee) === 'per_trip'">
            <div class="flex-item">
              <p>
                {{ $t('FREIGHT_RATE') }} : <strong>{{consignee.calculations.freight.freight_rate.toFixed(2)}}</strong>
              </p>
              <p>
                {{ $t('MIN_RATE') }} : <strong>{{consignee.calculations.freight.min_rate}}</strong>
              </p>
            </div>
            <div class="flex-item text-right ml-auto">
              <p>
                {{ $t('FREIGHT_UNIT') }} : <strong>{{getUnitDisplayName(consignee)}}</strong>
              </p>
            </div>
          </div>
          <div class="flex-container panel-head align-top mb-10" v-else-if="['per_case', 'per_kg', 'per_km', 'percentage'].includes(getUnitGroup(consignee))">
            <div class="flex-item">
              <p>
                {{ $t("FREIGHT_RATE") }} : <strong>
                  {{ consignee.calculations && consignee.calculations.freight && consignee.calculations.freight.freight_rate }}
                </strong>  &nbsp;
                 <Tooltip class="item" effect="dark" placement="bottom">
                    <div slot="content" class="tooltipcontent">
                      Slab Rates
                    </div>
                    <a href="#"
                    v-if="(getUnitGroup(consignee) === 'per_km' || consignee.materials.length) && showSlabRangeDetails"
                    v-on:click="showSlabRangeDetails = !showSlabRangeDetails"
                    style="text-decoration: none;
                    color: inherit;">
                    <svgicon name="icon-currency" width="16" height="12" stroke-width="2%"/></a>
                </Tooltip>
              </p>
              <p>
                {{ $t('MIN_RATE') }} : <strong>{{consignee.calculations && consignee.calculations.freight && consignee.calculations.freight.min_rate.toFixed(2)}}</strong>
              </p>
              <p style="text-transform: capitalize;">
                {{ $t('INDENT_MOVEMENT') }} : <strong>{{consignee.calculations.freight.movement}}</strong>
              </p>
            </div>
            <div class="flex-item">
                <p>
                {{ $t("FREIGHT_UNIT") }} : <strong>{{getUnitDisplayName(consignee)}}</strong>
              </p>
              <p>
                {{ $t('QUANTITY_SHORT_FORM') }} :
                <strong>{{getTotalQuantity()}}</strong>
              </p>
            </div>
          </div>
          <div class="flex-container panel-head align-top mb-10"
          v-else-if="getUnitGroup(consignee) === 'per_box' ||
          getUnitGroup(consignee) === 'per_package'">

            <div class="flex-item">
              <p>
                {{ $t('FREIGHT_UNIT') }} : <strong>{{getUnitDisplayName(consignee)}}</strong>
              </p>
              <p>
                {{ $t('MIN_RATE') }} : <strong>{{consignee.calculations.freight.min_rate}}</strong>
              </p>
            </div>
            <div v-for="(consignee,index) in indent.consignee_data" :key="index">
              <div class="flex-item" v-for="(material,indexmat) in consignee.materials"
              :key="indexmat">
                <p>
                  {{material.name}}: <strong>{{material.freight_charge.toFixed(2)}}</strong>
                </p>
                <p>
                  {{ $t('QUANTITY_SHORT_FORM') }}: <strong>{{material.quantity}}</strong>
                </p>
              </div>
            </div>
          </div>
      </div>

      <div v-if="!showSlabRangeDetails">
        <Collapse accordion class="collapse-slider">
          <CollapseItem v-for="(consignee,index) in indent.consignee_data" :key="index" :title="getTitleForSlab(consignee)">
            <div class="flex-item text-right ml-auto mb-20">
            <p>
                  {{ $t('QUANTITY_SHORT_FORM') }}: <strong>{{getQuantityPerConsignee(consignee.materials)}}</strong>
            </p>
            </div>
            <div>
              <Row class="grid-header header-plain header-collapse px-0 pt-0">
                <Col :span="6">
                  <div class="cell"><strong>{{ $t('PAY_SLAB_RANGE') }}</strong></div>
                </Col>
                <Col :span="8">
                  <div class="cell"><strong>{{ $t('MDM_PRODUCT_CATEGORY') }}</strong></div>
                </Col>
                <Col :span="5">
                  <div class="cell"><strong>{{ $t('MIN_RATE') }}</strong></div>
                </Col>
                <Col :span="5">
                  <div class="cell text-right"><strong>{{ $t('PAY_RATE') }}</strong></div>
                </Col>
              </Row>
              <Collapse
                highlight="true"
                class="collapse-table bordered"
                disable="arrow">
                  <CollapseItem
                  v-for="(material,index) in getSlabRanges(consignee.materials)"
                  :key="index"
                  disabled
                  not-allowed>
                    <template slot="title">
                      <Row type="flex" class="grid-collapse grid-bordered p-0" style="cursor:default;">
                        <Col :span="6">
                          <div class="cell">
                            <span>{{ material.min }} - {{ material.max }}</span>
                          </div>
                        </Col>
                        <Col :span="8">
                          <div class="cell bg-white">
                            <span>{{ material.name }}</span>
                          </div>
                        </Col>
                        <Col :span="5">
                          <div class="cell bg-white">
                            <span>{{ material.min_rate }}</span>
                          </div>
                        </Col>
                        <Col :span="5">
                          <div class="cell text-right">
                            <span>{{ material.freight_charge.toFixed(2) }}</span>
                          </div>
                        </Col>
                      </Row>
                    </template>
                  </CollapseItem>
              </Collapse>
            </div>
          </CollapseItem>
        </Collapse>
      </div>
    <UploadDocumentModal
      v-show="isConsolidatedEnabled"
      :visible="uploadModalVisible"
      :destination="activeDestination || {}"
      :indent-no="(indent && indent.order_no) || ''"
      :movement="movementSuffix"
      @close="uploadModalVisible = false"
      @uploaded="onAttachmentUploaded"
    />
    <portal to="file-preview">
      <div v-if="filePreviewVisible" class="trip-preview-wrapper">
        <button
          v-if="filePreviewIndex > 0"
          class="trip-preview-nav trip-preview-nav--prev"
          @click="filePreviewNavigate(-1)"
        ><i class="el-icon-arrow-left"></i></button>
        <FilePreview
          :file-url="filePreviewUrl"
          :original-file-name="filePreviewName"
          @close="filePreviewVisible = false"
        />
        <button
          v-if="filePreviewIndex < filePreviewFiles.length - 1"
          class="trip-preview-nav trip-preview-nav--next"
          @click="filePreviewNavigate(1)"
        ><i class="el-icon-arrow-right"></i></button>
        <span class="trip-preview-counter">{{ filePreviewIndex + 1 }} / {{ filePreviewFiles.length }}</span>
      </div>
    </portal>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { timelineToShowUsers } from '@common/constants/timelineConstants';
import { getRouteByDepot } from '@common/lib/payments';
import { unitGroups, unitDisplayNames } from '@common/constants/tripDetailsConstants';
import Attachments from '@common/components/Attachments';
import AttachmentsMin from '@common/components/AttachmentsMin';
import UploadDocumentModal from '@/modules/indents/components/UploadDocumentModal';
import FilePreview from '@common/components/FilePreview';
import VolumeFormulaConvertor from '../../../../../common-v2/helpers/volume-formula-converter';

export default {
  name: 'trip-details',
  components: {
    Attachments,
    AttachmentsMin,
    UploadDocumentModal,
    FilePreview,
  },
  data() {
    return {
      timelineToShowUsers, showSlabRangeDetails: true, showSlabs: false,
      uploadModalVisible: false,
      activeDestination: null,
      filePreviewVisible: false,
      filePreviewUrl: '',
      filePreviewName: '',
      filePreviewFiles: [],
      filePreviewIndex: 0,
    };
  },
  props: {
    indent: {
      default: () => {},
      type: Object,
    },
  },
  created() {},
  watch: {
    indent(newVal) {
      this.indent = newVal;
    },
  },
  computed: {
    ...mapGetters({
      configurations: 'app/currentClientConfig',
    }),
    isConsolidatedEnabled() {
      return this.$hasFeature('transporter_consolidated_attachment_management');
    },
    movementSuffix() {
      const map = { 1: 'secondary', 2: 'primary', 3: 'vendor', 4: 'return' };
      return map[this.indent && this.indent.movement] || 'primary';
    },
    lrAttachmentsLimit() {
      return (this.configurations && this.configurations.attachments_limit &&
        this.configurations.attachments_limit.lr) ? this.configurations.attachments_limit.lr : 5;
    },
    getIndentRoute() {
      const result = getRouteByDepot(this.indent);
      const tooltip = result.multidropCities;
      const route = result.source.concat(' to ', result.destination);
      return { route, tooltip };
    },
    getAttachments() {
      return this.indent.consignee_data.map(consignee => consignee.attachments.map(attachment => ({
        name: attachment.content_file_name,
        url: attachment.metadata.location,
        id: attachment._id,
      })));
    },
    getAssociated() {
      const associatedIndents = this.indent.associated_indents;
      if (associatedIndents && associatedIndents.length) {
        return associatedIndents.map(indent => indent.order_no || '').join();
      }
      return '';
    },
    preferredVolumeUnit() {
      return this.$preferredVolumeUnit();
    },
  },
  methods: {
    getConvertedClientVolume(volume) {
      const volumeInClientUnit = new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, volume);
      return volumeInClientUnit;
    },
    fixTwoDigits(val) {
      const value = Number(val);
      return parseFloat(value.toFixed(2));
    },
    preview(data) {
      this.$emit('preview', data);
    },
    getClassOfMessage(event) {
      if (event.type === 'destination') {
        return 'dot-green';
      } else if (event.message === 'reported at') {
        return 'dot-blue';
      }
      return 'dot-purple';
    },
    getUnitGroup(consignee) {
      return consignee.calculations && consignee.calculations.freight &&
        unitGroups[consignee.calculations.freight.unit];
    },
    getTitleForSlab(consignee) {
      return `${consignee.name} - ${consignee.ref_id} - ${consignee.city}`;
    },
    getTotalQuantity() {
      return this.indent.consignee_data.reduce((acc, consignee) => {
        let _acc = acc;
        const materials = consignee.materials || [];
        const materialQuantity = materials.reduce(
          (quantity, material) => quantity + parseInt(material && material.quantity, 10), 0);
        _acc += materialQuantity;
        return _acc;
      }, 0);
    },
    getQuantityPerConsignee(materials) {
      return materials.reduce((acc, material) => acc + material.quantity, 0);
    },
    getUnitDisplayName(consignee) {
      return consignee.calculations && consignee.calculations.freight &&
        unitDisplayNames[consignee.calculations.freight.unit];
    },
    getSlabRanges(materials) {
      const result = materials.reduce((materialSlabRanges, material) => {
        if (!material.slabs.length) {
          materialSlabRanges.push({
            name: material.name,
            freight_charge: material.freight_charge,
            quantity: material.quantity,
            min: '',
            max: '',
            min_rate: material.minimum_rate,
          });
        }
        for (let i = 0; i < material.slabs.length; i += 1) {
          materialSlabRanges.push({
            name: material.name,
            freight_charge: material.slabs[i].rate,
            quantity: material.slabs[i].slab && material.slabs[i].slab.qty,
            min: material.slabs[i].slab && material.slabs[i].slab.min,
            max: material.slabs[i].slab && material.slabs[i].slab.max,
            min_rate: material.slabs[i].min_rate,
          });
        }
        return materialSlabRanges;
      }, []);
      return result;
    },
    previewConsigneeAttachments(index) {
      const attachments = this.getAttachments[index];
      if (!attachments || !attachments.length) return;
      this.filePreviewFiles = attachments;
      this.filePreviewIndex = 0;
      this.filePreviewUrl = attachments[0].url;
      this.filePreviewName = attachments[0].name || '';
      this.filePreviewVisible = true;
    },
    filePreviewNavigate(dir) {
      const next = this.filePreviewIndex + dir;
      if (next < 0 || next >= this.filePreviewFiles.length) return;
      this.filePreviewIndex = next;
      this.filePreviewUrl = this.filePreviewFiles[next].url;
      this.filePreviewName = this.filePreviewFiles[next].name || '';
    },
    async addAttachments(file, index) {
      if (this.isConsolidatedEnabled) {
        const consignee = this.indent.consignee_data[index];
        this.activeDestination = {
          location_id: consignee.id,
          gate_id: consignee.gate_id || null,
          name: consignee.name,
        };
        this.uploadModalVisible = true;
        return;
      }
      try {
        const formData = new FormData();
        formData.append('attachment', file.raw);
        const result = await this.$store.dispatch(
          'payment/uploadDocument',
          formData,
        );
        if (result && result.error) {
          this.$error({ message: this.$getErr('UPLOAD_FAILS') });
        } else {
          this.saveLorryReceipt(result, index);
        }
      } catch (err) {
        this.$error({ message: this.$getErr('UPLOAD_FAILS') });
      }
    },
    async onAttachmentUploaded() {
      this.uploadModalVisible = false;
      try {
        const response = await this.$store.dispatch('indents/listV2Attachments', {
          indentNo: this.indent.order_no,
        });
        const list = Array.isArray(response) ? response : (response && response.data) || [];
        const updatedConsigneeData = this.indent.consignee_data.map((consignee) => {
          const destData = list.find(
            d => (consignee.gate_id || consignee.id) &&
              (consignee.gate_id || consignee.id).toString() === d.destination_id,
          );
          if (!destData) return consignee;
          return {
            ...consignee,
            attachments: [
              ...(destData.lr_attachments || []),
              ...(destData.pod_attachments || []),
              ...(destData.other_attachments || []),
            ],
          };
        });
        this.indent = { ...this.indent, consignee_data: updatedConsigneeData };
      } catch (err) {
        // non-critical
      }
    },
    async saveLorryReceipt(result, index) {
      try {
        const selectedConsignee = this.indent.consignee_data[index];
        const lorryReceipts = {
          destination_id: selectedConsignee.consignee_id,
          source_location_id: selectedConsignee.source_location_id,
          lr_attachment_ids: [result.id],
          ...(this.configurations && this.configurations.multi_drop_at_gate_level && selectedConsignee.gate_id
            ? { gate_id: selectedConsignee.gate_id }
            : {}),
        };
        const orderId = this.indent.order_no;
        const response = await this.$store.dispatch('payment/saveLorryReceipt', {
          orderId,
          data: [lorryReceipts],
        });
        if (response && response.error) {
          this.$error({ message: this.$getErr('UPLOAD_FAILS') });
        } else {
          this.$emit('addIndentDetails', result.data, 'attachments');
        }
      } catch (err) {
        this.$error({ message: this.$getErr('UPLOAD_FAILS') });
      }
    },
  },
};
</script>

<style>
.trip-preview-wrapper {
  position: relative;
}
.trip-preview-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10000000;
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  font-size: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.trip-preview-nav:hover { background: rgba(255,255,255,0.3); }
.trip-preview-nav--prev { left: 16px; }
.trip-preview-nav--next { right: 16px; }
.trip-preview-counter {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000000;
  color: #fff;
  font-size: 13px;
  background: rgba(0,0,0,0.5);
  padding: 4px 12px;
  border-radius: 12px;
}
</style>
